import { Injectable } from '@angular/core';
import {
  CommonToggleService,
  InstallerModeLockKey,
  SharedErrors,
  ToastMessageService,
  Utils,
  WallboxProductCodeRegex,
} from '@kw-shared/common';
import { TranslateService } from '@ngx-translate/core';
import {
  BleKey,
  CertificateResult,
  ConfigurationPropertyKey,
  ConfigurationResult,
  ConfigurationType,
  ExternalMeterProviders,
  ExternalMeterTestOptions,
  InstallerSettingType,
  IoKey,
  KebaWallboxPlugin,
  MvaRecordType,
  MvaWallboxResults,
  NetworkInterface,
  ObjectKeys,
  RestapiKey,
  WallboxConfiguration,
  WallboxInfoP40,
} from 'apps/keba-wallbox-app/src/plugins/keba-wallbox-plugin';
import { NetworkConfiguration } from 'apps/keba-wallbox-app/src/plugins/network-configuration';
import { BehaviorSubject } from 'rxjs';
import { SavedWallbox } from '../../../pages/select-wallbox/interface/saved-wallbox.interface';
import { CurrentWallboxConfigService } from '../../current-wallbox-config.service';
import { LocalErrorIdentifier } from '../local-error-handler';
import { Router } from '@angular/router';

export type WallboxAppConfiguration = WallboxConfiguration & { password?: string };

@Injectable({
  providedIn: 'root',
})
export class LocalWallboxSettingsService {
  private readonly installerModeLockMapping: { [key: string]: ConfigurationPropertyKey[] } = {
    [InstallerModeLockKey.LOCK_X1A]: [IoKey.INST_X1_1_ENABLE, IoKey.INST_X1_1_MODE, IoKey.INST_X1_1_VALUE],
    [InstallerModeLockKey.LOCK_X1B]: [IoKey.INST_X1_2_ENABLE, IoKey.INST_X1_2_MODE, IoKey.INST_X1_2_VALUE],
    [InstallerModeLockKey.LOCK_X2]: [IoKey.INST_X2_ENABLE, IoKey.INST_X2_MODE],
  };
  private authenticationFailed = new BehaviorSubject<SavedWallbox | null>(null);
  public authenticationFailed$ = this.authenticationFailed.asObservable();
  private mvaRecordsSyncPercentage$ = new BehaviorSubject<number>(0);

  constructor(
    private toastMessageService: ToastMessageService,
    private currentWallboxConfigService: CurrentWallboxConfigService,
    private translateService: TranslateService,
    private router: Router,
    private toggleService: CommonToggleService
  ) {}

  public getMvaRecordsSyncPercentage$(): BehaviorSubject<number> {
    return this.mvaRecordsSyncPercentage$;
  }

  public async connectToWallboxHotspot(ssid: string, password: string): Promise<void> {
    try {
      return await NetworkConfiguration.connect({
        ssid,
        password,
      });
    } catch (error) {
      this.toastMessageService.setError('connectToWallboxHotspot() error ' + error);
      return await Promise.reject();
    }
  }

  public async connectToWallboxWlan(ssid: string, password: string): Promise<void> {
    try {
      return await NetworkConfiguration.connect({
        ssid,
        password,
      });
    } catch (error) {
      this.toastMessageService.setError('connectToWallboxWlan() error ' + error);
      return await Promise.reject();
    }
  }

  // TODO check error handling!!
  // SETTINGS
  public async readConfiguration(
    configurationType: ConfigurationType,
    propertyKey?: ConfigurationPropertyKey | string,
    wallboxConfig?: WallboxAppConfiguration
  ): Promise<void | ObjectKeys> {
    const config = wallboxConfig ?? this.getWallboxConfig();

    if (!config) {
      this.toastMessageService.setError('readConfiguration() ng: missing config!');
      return;
    }
    return KebaWallboxPlugin.readConfiguration({ config, configurationType, propertyKey })
      .then(result => {
        const object = this.convertToObjectKeys(result);
        if (object) {
          this.toggleService.updateConfiguration(object);
          return Promise.resolve(object);
        }
        return Promise.reject();
      })
      .catch(error => {
        let extractedError = Utils.extractErrorMessage(error);
        if (extractedError.indexOf(LocalErrorIdentifier.REST_TOKEN_INVALID) >= 0) {
          if (Utils.hasNonEmptyProperty(config, 'productCode')) {
            let productCode = config['productCode'];
            this.router.navigate(['connect'], {
              queryParams: {
                address: config.address,
                serialNumber: config.serialNumber,
                model: productCode.match(WallboxProductCodeRegex.IS_P40)
                  ? 'P40'
                  : productCode.match(WallboxProductCodeRegex.IS_P30)
                    ? 'P30'
                    : 'M20',
                productCode: productCode,
                channel: config.channel,
                verificationMode: config.verificationMode,
              },
            });
          }
        }
        this.toastMessageService.setError('readConfiguration() for ' + configurationType + ' ' + error);
        return;
      });
  }

  public async readWallboxInfoP40(
    config?: WallboxAppConfiguration,
    hideAuthenticationError?: boolean
  ): Promise<undefined | WallboxInfoP40> {
    const configData = config ? config : this.getWallboxConfig();

    if (!configData) {
      this.toastMessageService.setError('readConfiguration() ng: missing config!');
      return;
    }

    return KebaWallboxPlugin.readWallboxInfoP40({ config: configData })
      .then(result => {
        if (result) {
          return Promise.resolve(result);
        }
        return Promise.reject();
      })
      .catch(error => {
        if (error.message === LocalErrorIdentifier.AUTHENTICATION_FAILED) {
          this.authenticationFailed.next({
            serialNumber: configData.serialNumber,
            verificationMode: configData.verificationMode,
            availableChannels: [
              {
                address: configData.address,
                channel: configData.channel,
              },
            ],
          });
        }
        if (!hideAuthenticationError && error.message !== LocalErrorIdentifier.AUTHENTICATION_FAILED) {
          this.toastMessageService.setError('readConfiguration() for ' + error);
        }
        return Promise.reject();
      });
  }

  public async readPowerConfiguration(): Promise<ConfigurationResult | void> {
    const config = this.getWallboxConfig();
    if (!config) {
      this.toastMessageService.setError('readConfiguration() ng: missing config!');
      return;
    }
    return KebaWallboxPlugin.readPowerConfiguration({ config })
      .then(result => {
        if (result) {
          return Promise.resolve(result);
        }
        return Promise.reject();
      })
      .catch(error => {
        this.toastMessageService.setError('readConfiguration() for ' + error);
        return Promise.reject();
      });
  }

  public async writeConfiguration(
    configurationType: ConfigurationType,
    configurationOptions: ConfigurationResult
  ): Promise<{ restartRequired: boolean }> {
    const config = this.getWallboxConfig();
    if (!config) {
      this.toastMessageService.setError('writeConfiguration() ng: missing config!');
      return { restartRequired: false };
    }
    return KebaWallboxPlugin.writeConfiguration({ config, configurationType, configurationOptions }).catch(error => {
      this.toastMessageService.setError('writeConfiguration() for ' + configurationType + ' ' + error);
      return Promise.reject();
    });
  }

  // writeCommissioningModeActiveState is not supported in the current version of the wallbox
  public async writeCommissioningModeActiveState(active: boolean): Promise<void> {
    const config = this.getWallboxConfig();
    if (!config) {
      this.toastMessageService.setError('writeCommissioningModeState() ng: missing config!');
      return Promise.reject();
    }

    try {
      await KebaWallboxPlugin.writeCommissioningModeState({
        config,
        active,
      });
    } catch (error) {
      this.toastMessageService.setError('writeCommissioningModeState() error ' + error);
      return Promise.reject('ENDPOINT_ERROR');
    }
    return;
  }

  public async readNetworkInterfaces(): Promise<{
    interfaces: NetworkInterface[];
  }> {
    const config = this.getWallboxConfig();
    if (!config) {
      this.toastMessageService.setError('writeConfiguration() ng: missing config!');
      return { interfaces: [] };
    }

    return KebaWallboxPlugin.readNetworkInterfaces({ config }).catch(error => {
      this.toastMessageService.setError('writeConfiguration() for ' + config + ' ' + error);
      return Promise.reject();
    });
  }

  public async testExternalMeterConnection(testOptions: ExternalMeterTestOptions): Promise<void> {
    const config = this.getWallboxConfig();
    if (!config) {
      this.toastMessageService.setError('testExternalMeterConnection() ng: missing config!');
      return;
    }

    return KebaWallboxPlugin.testExternalMeterConnection({ config, testOptions })
      .then(() => {
        this.toastMessageService.setSuccess(
          this.translateService.instant('kwa_lbl_settings_external_tcp_meter_test_connection_success')
        );
      })
      .catch(error => {
        this.toastMessageService.setError('testExternalMeterConnection() error ' + error);
        return;
      });
  }

  public async readExternalMeterProviders(): Promise<ExternalMeterProviders> {
    const config = this.getWallboxConfig();
    if (!config) {
      this.toastMessageService.setError('readExternalMeterProviders() ng: missing config!');
      return Promise.reject();
    }

    return KebaWallboxPlugin.readExternalMeterProviders({ config });
  }

  public async readCertificates(): Promise<CertificateResult[]> {
    const config = this.getWallboxConfig();
    if (!config) {
      this.toastMessageService.setError('readCertificates() ng: missing config!');
      return Promise.reject();
    }

    return KebaWallboxPlugin.readCertificates({ config })
      .then(result => {
        return Promise.resolve(result.certificates);
      })
      .catch(error => {
        this.toastMessageService.setError('readCertificates() error ' + error);
        return Promise.reject();
      });
  }

  public async installOcppCertificate(path: string, password: string): Promise<void> {
    const config = this.getWallboxConfig();
    if (!config) {
      this.toastMessageService.setError('installOcppCertificate() ng: missing config!');
      return Promise.reject();
    }

    return KebaWallboxPlugin.installOcppCertificate({ config, path, password }).catch(error => {
      if (error.message !== SharedErrors.CANCELLED_BY_USER) {
        this.toastMessageService.setError('installOcppCertificate() error ' + error);
        return Promise.reject('ENDPOINT_ERROR');
      }
      return Promise.reject();
    });
  }

  public async lockInstallerSettings(lockKeys: InstallerModeLockKey[]): Promise<void> {
    const config = this.getWallboxConfig();
    if (!config) {
      this.toastMessageService.setError('readLockedInstallerSettings() ng: missing config!');
      return Promise.reject();
    }

    try {
      await KebaWallboxPlugin.lockInstallerSettings({
        config,
        settingKeys: lockKeys.flatMap(key => this.installerModeLockMapping[key]),
      });
      this.toastMessageService.setSuccess();
    } catch (error) {
      this.toastMessageService.setError('lockInstallerSettings() error ' + error);
      return Promise.reject('ENDPOINT_ERROR');
    }
    return;
  }

  public async readLockedInstallerSettings(): Promise<any> {
    const config = this.getWallboxConfig();
    if (!config) {
      this.toastMessageService.setError('readLockedInstallerSettings() ng: missing config!');
      return Promise.reject();
    }

    try {
      const result = await KebaWallboxPlugin.readLockedInstallerSettings({ config });
      const configuration: any = {};
      Object.keys(this.installerModeLockMapping).forEach(key => {
        configuration[key] = String(this.installerModeLockMapping[key].every(k => result.settingKeys.includes(k)));
      });
      return configuration;
    } catch (error) {
      this.toastMessageService.setError('readLockedInstallerSettings() error ' + error);
      return Promise.reject('ENDPOINT_ERROR');
    }
  }

  public async installCentralCertificate(path: string): Promise<void> {
    const config = this.getWallboxConfig();
    if (!config) {
      this.toastMessageService.setError('installCentralCertificate() ng: missing config!');
      return Promise.reject();
    }

    return KebaWallboxPlugin.installCentralCertificate({ config, path }).catch(error => {
      if (error.message !== SharedErrors.CANCELLED_BY_USER) {
        this.toastMessageService.setError('installCentralCertificate() error ' + error);
        return Promise.reject('ENDPOINT_ERROR');
      }
      return Promise.reject();
    });
  }

  public async readMvaWallboxes(): Promise<MvaWallboxResults> {
    const config = this.getWallboxConfig();
    if (!config) {
      this.toastMessageService.setError('readMvaWallboxes() ng: missing config!');
      return Promise.reject();
    }

    return KebaWallboxPlugin.readMvaWallboxes({ config }).catch(error => {
      this.toastMessageService.setError('readMvaWallboxes() error ' + error);
      return Promise.reject();
    });
  }

  public async synchronizeMvaRecords(type: MvaRecordType, serialNumber?: string, section?: string): Promise<void> {
    const config = this.getWallboxConfig();
    if (!config) {
      this.toastMessageService.setError('synchronizeMvaRecords() ng: missing config!');
      return Promise.reject();
    }

    return KebaWallboxPlugin.synchronizeMvaRecords({
      config,
      serialNumber: serialNumber || config.serialNumber,
      type,
      section,
    }).catch(error => {
      if (error && error.message?.includes(LocalErrorIdentifier.VALIDATION_RANGE_MAX_FAILURE)) {
        this.toastMessageService.setError('kwa.local-error.validation_range_max_failure.message', true);
      }
      return Promise.reject();
    });
  }

  public async readMvaSynchronizationStatus(
    type: MvaRecordType,
    serialNumber?: string
  ): Promise<{ missingRecordsCount: number; msg: string } | null> {
    const config = this.getWallboxConfig();
    if (!config) {
      this.toastMessageService.setError('readMvaSynchronizationStatus() ng: missing config!');
      return null;
    }

    return KebaWallboxPlugin.readMvaSynchronizationStatus({
      config,
      serialNumber: serialNumber || config.serialNumber,
      type,
    })
      .then(result => {
        if (!result || result.totalRecordsCount === 0) {
          return { missingRecordsCount: 0, msg: 'No records to sync' }; // Prevent division by zero
        }

        // Compute missingRecords
        const missingRecordsCount = result.totalRecordsCount - result.retrievedRecordsCount;

        // Compute percentage correctly
        let percentage =
          missingRecordsCount === 0 ? 100 : Math.floor((result.retrievedRecordsCount / result.totalRecordsCount) * 100);

        // Update observable
        this.mvaRecordsSyncPercentage$.next(percentage);

        return {
          missingRecordsCount,
          msg: '',
        };
      })
      .catch(error => {
        this.toastMessageService.setError('readMvaSynchronizationStatus() error ' + error);
        return null;
      });
  }

  public async exportMvaRecords(type: MvaRecordType, serialNumber?: string): Promise<{ path: string }> {
    const config = this.getWallboxConfig();
    if (!config) {
      this.toastMessageService.setError('exportMvaRecords() ng: missing config!');
      return Promise.reject();
    }

    return KebaWallboxPlugin.exportMvaRecords({
      config,
      serialNumber: serialNumber || config.serialNumber,
      type,
    }).catch(error => {
      this.toastMessageService.setError('exportMvaRecords() error: ' + error);
      return Promise.reject();
    });
  }

  public async exportDiagnostics(): Promise<{ path: string }> {
    const config = this.getWallboxConfig();
    if (!config) {
      this.toastMessageService.setError('exportDiagnostics() ng: missing config!');
      return Promise.reject();
    }

    return KebaWallboxPlugin.exportDiagnostics({ config }).catch(error => {
      this.toastMessageService.setError('exportDiagnostics() error ' + error);
      return Promise.reject();
    });
  }

  public async exportSecurityLogs(): Promise<{ path: string }> {
    const config = this.getWallboxConfig();
    if (!config) {
      this.toastMessageService.setError('exportSecurityLogs() ng: missing config!');
      return Promise.reject();
    }

    return KebaWallboxPlugin.exportSecurityLogs({ config }).catch(error => {
      this.toastMessageService.setError('exportSecurityLogs() error ' + error);
      return Promise.reject();
    });
  }

  public async installWebCertificate(path: string, password: string): Promise<void> {
    const config = this.getWallboxConfig();
    if (!config) {
      this.toastMessageService.setError('installWebCertificate() ng: missing config!');
      return Promise.reject();
    }

    return KebaWallboxPlugin.installWebCertificate({ config, path, password }).catch(error => {
      if (error.message !== SharedErrors.CANCELLED_BY_USER) {
        this.toastMessageService.setError('installWebCertificate() error ' + error);
        return Promise.reject('ENDPOINT_ERROR');
      }
      return Promise.reject();
    });
  }

  public async readWebCertificates(): Promise<boolean> {
    try {
      const config = this.getWallboxConfig();
      if (!config) {
        this.toastMessageService.setError('readWebCertificates() ng: missing config!');
        return Promise.reject(false);
      }

      const { hasCertificates } = await KebaWallboxPlugin.readWebCertificates({ config });
      return hasCertificates;
    } catch (error) {
      this.toastMessageService.setError('Error reading web certificates: ' + error);

      return false;
    }
  }

  public getWallboxConfig(): WallboxAppConfiguration | undefined {
    const wallboxConfig = this.currentWallboxConfigService.getCurrentWallboxConfig();
    if (wallboxConfig?.isLocalWallbox()) {
      return wallboxConfig.toWallboxConfiguration();
    }
    return undefined;
  }

  public convertToObjectKeys(configuration?: ConfigurationResult): ObjectKeys | undefined {
    return configuration?.configs?.reduce((obj, cur) => ({ ...obj, [cur.key]: cur.value }), {});
  }

  public async getWallboxDeviceName(config?: WallboxAppConfiguration): Promise<string | undefined> {
    try {
      const response = await this.readConfiguration(ConfigurationType.RESTAPI, RestapiKey.ALIAS, config);
      return response?.[RestapiKey.ALIAS] || undefined;
    } catch (error) {
      return undefined;
    }
  }

  public async getBleWallboxDeviceName(config?: WallboxAppConfiguration): Promise<string | undefined> {
    try {
      const response = await this.readConfiguration(ConfigurationType.BLE, BleKey.BLE_DEVICE_NAME, config);
      return response?.[RestapiKey.ALIAS] || undefined;
    } catch (error) {
      return undefined;
    }
  }

  async readInstallerSettingValues(
    config: WallboxConfiguration,
    settingsType: InstallerSettingType
  ): Promise<string[]> {
    try {
      const response = await KebaWallboxPlugin.readInstallerSettingValues({
        config,
        settingsType,
      });

      return response?.values ?? [];
    } catch (error) {
      console.error('Error reading installer setting values:', error);
      return [];
    }
  }
}
